/* ==========================================================================
   fm-alt-match.js — RAPPROCHEMENT « TEXTE ALTERNATIF » ↔ « NOM DE VARIANTE »
   --------------------------------------------------------------------------
   Chargement (theme.liquid), AVANT product-card.js et product.js :
     <script src="{{ 'fm-alt-match.js' | asset_url }}" defer></script>

   LE PROBLÈME
   Jusqu'ici, une image n'était rattachée à une variante que si son texte
   alternatif était STRICTEMENT identique au nom de la variante. Écrire
   « Nude » pour la variante « 01 | Nude » ne suffisait pas : l'image était
   alors considérée comme commune et s'affichait sur toutes les variantes.

   LE PRINCIPE RETENU : LES MOTS QUI DISTINGUENT
   Plutôt que de comparer des chaînes, on compare des MOTS, et seulement ceux
   qui servent réellement à distinguer une variante d'une autre.

     1. On découpe les noms de variantes en mots.
        « 01 | Nude »       -> 01, nude
        « 02 | Nude Rosé »  -> 02, nude, rose   (accents retirés)

     2. On jette les mots présents dans TOUTES les variantes : ils ne
        distinguent rien. Ici « nude » est dans les deux -> jeté.
        Il reste : 01  /  02, rose

     3. Le texte alternatif est réduit à ces mots-là. Le reste
        (« packshot », « photo », « fond blanc »…) est ignoré.

     4. La variante qui partage le PLUS de mots avec l'alt l'emporte. En cas
        d'égalité, l'alt est considéré comme ambigu et l'image redevient
        commune — c'est volontaire : mieux vaut une image affichée partout
        qu'une image rattachée à la mauvaise variante et donc invisible là où
        elle devrait être. Un avertissement est écrit dans la console.

   CE QUE ÇA DONNE, avec les variantes « 01 | Nude » et « 04 | Rosewood » :
     « 01 | Nude »          -> 01 | Nude      (identique, comme avant)
     « Nude »               -> 01 | Nude      (nouveau)
     « nude »               -> 01 | Nude      (la casse n'importe plus)
     « 01 »                 -> 01 | Nude      (le numéro suffit)
     « Nude packshot »      -> 01 | Nude      (le mot en trop est ignoré)
     « Rosewood - fond blanc » -> 04 | Rosewood
     « Photo produit »      -> commune, affichée partout
     (rien, alt vide)       -> commune, affichée partout
     « all »                -> commune, affichée partout (mot réservé)
   Et avec « 01 | Nude » et « 02 | Nude Rosé » :
     « Nude »               -> ambigu -> commune (le mot ne tranche pas)
     « Nude Rosé »          -> 02 | Nude Rosé
     « 01 »                 -> 01 | Nude

   Diagnostic : window.FMAlt.debug = true dans la console pour voir, image par
   image, ce qui a été rapproché de quoi.
   ========================================================================== */
(function () {
  'use strict';

  /* Minuscules, accents retirés, séparateurs (| / - _ . , : ; …) transformés
     en espaces. « 01 | Nude » et « 01-nude » deviennent la même chose. */
  function normalise(s) {
    s = (s === 0 ? '0' : (s || '')).toString();
    try { s = s.normalize('NFD').replace(/[̀-ͯ]/g, ''); } catch (e) {}
    return s
      .toLowerCase()
      .replace(/[|\/\\\-–—_.,:;!?()\[\]{}"'’«»+*&#]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* Découpage en mots. Les mots d'une seule lettre sont écartés (trop de faux
     rapprochements), sauf si la variante ne s'appelle QUE comme ça — d'où le
     repli ci-dessous. */
  function mots(s) {
    var bruts = normalise(s).split(' ').filter(Boolean);
    var utiles = bruts.filter(function (m) { return m.length >= 2; });
    return utiles.length ? utiles : bruts;
  }

  function uniq(a) {
    var vu = {}, out = [];
    for (var i = 0; i < a.length; i++) if (!vu[a[i]]) { vu[a[i]] = 1; out.push(a[i]); }
    return out;
  }

  /* Prépare, pour une liste de noms de variantes, la table des mots
     DISTINCTIFS de chacune. Le résultat est mis en cache : la même liste
     revient à chaque image, à chaque survol, à chaque changement de variante.
     La clé du cache est la liste elle-même, donc elle se périme d'elle-même
     si les variantes changent. */
  var _cache = {};
  /* Intitulés déjà signalés comme ambigus (un avertissement, pas cent). */
  var _avertis = {};
  function table(noms) {
    noms = (noms || []).filter(function (n) { return normalise(n) !== ''; });
    if (!noms.length) return null;

    var cle = noms.join('\u0000');
    if (_cache[cle]) return _cache[cle];

    var parNom = noms.map(function (n) { return { nom: n, mots: uniq(mots(n)) }; });

    /* Combien de variantes contiennent chaque mot. Un mot présent dans
       TOUTES ne distingue rien -> on le retire. Avec une seule variante, on
       ne retire rien (sinon il ne resterait aucun mot). */
    var freq = {};
    parNom.forEach(function (e) {
      e.mots.forEach(function (m) { freq[m] = (freq[m] || 0) + 1; });
    });

    var vocabulaire = {};
    parNom.forEach(function (e) {
      e.distinctifs = parNom.length > 1
        ? e.mots.filter(function (m) { return freq[m] < parNom.length; })
        : e.mots.slice();
      e.distinctifs.forEach(function (m) { vocabulaire[m] = 1; });
    });

    var t = { entrees: parNom, vocabulaire: vocabulaire };
    _cache[cle] = t;
    return t;
  }

  /* Rend le nom de variante (tel qu'il a été passé dans `noms`) auquel ce
     texte alternatif se rapporte, ou '' si l'image est commune / ambiguë. */
  function match(alt, noms) {
    var t = table(noms);
    if (!t) return '';

    /* TEXTE ALTERNATIF VIDE = image commune à toutes les variantes.
       C'est le cas par défaut, et de loin le plus fréquent. */
    var altNorm = normalise(alt);
    if (!altNorm) return '';

    /* MOTS RÉSERVÉS = image commune, quoi qu'il y ait d'autre dans l'alt.
       C'est la porte de sortie explicite, pour les cas où le texte alternatif
       est utile au référencement ou à l'accessibilité et contient malgré soi
       un mot qui ressemble à un nom de variante :
         « all »                      -> toutes les variantes
         « all - swatch sur la peau » -> toutes les variantes aussi
       Le test passe AVANT tout le reste, donc il gagne toujours. Si jamais
       une de tes variantes s'appelle vraiment comme l'un de ces mots, retire
       le mot de la liste ci-dessous. */
    var motsAlt = mots(alt);
    for (var c = 0; c < motsAlt.length; c++) {
      if (FMAlt.communs.indexOf(motsAlt[c]) !== -1) {
        if (FMAlt.debug) console.log('[FMAlt] « ' + alt + ' » -> commune (mot réservé)');
        return '';
      }
    }

    /* 1. Identique au nom complet : on tranche tout de suite, sans passer par
          les mots. Garantit que le comportement d'avant est conservé à
          l'identique là où les alt sont déjà exacts. */
    for (var i = 0; i < t.entrees.length; i++) {
      if (normalise(t.entrees[i].nom) === altNorm) return t.entrees[i].nom;
    }

    /* 2. Sinon : on ne garde de l'alt que les mots qui distinguent une
          variante d'une autre. */
    var altMots = uniq(mots(alt)).filter(function (m) { return t.vocabulaire[m]; });
    if (!altMots.length) return '';

    var meilleur = -1, gagnants = [];
    t.entrees.forEach(function (e) {
      var n = 0;
      for (var k = 0; k < altMots.length; k++) {
        if (e.distinctifs.indexOf(altMots[k]) !== -1) n++;
      }
      if (n === 0) return;
      if (n > meilleur) { meilleur = n; gagnants = [e.nom]; }
      else if (n === meilleur) gagnants.push(e.nom);
    });

    if (gagnants.length === 1) {
      if (FMAlt.debug) console.log('[FMAlt] « ' + alt + ' » -> ' + gagnants[0]);
      return gagnants[0];
    }
    if (gagnants.length > 1) {
      /* Message volontairement neutre : ce helper ne sert plus qu'aux images.
         product.js s'en sert aussi pour les lignes de composition. Parler
         d'« image » ici enverrait sur une fausse piste.
         Averti UNE SEULE FOIS par intitulé : la fonction est rappelée à chaque
         changement de variante et à chaque passe sur la galerie, sans quoi la
         console se remplirait du même avertissement. */
      if (!_avertis[altNorm]) {
        _avertis[altNorm] = 1;
        console.warn('[FMAlt] Intitulé ambigu : « ' + alt + ' » correspond autant à ' +
          gagnants.join(' qu\'à ') + '. Il est traité comme commun à toutes les ' +
          'variantes (image affichée partout, ligne de composition affichée ' +
          'partout). Ajoute un mot qui tranche — le numéro, par exemple.');
      }
    } else if (FMAlt.debug) {
      console.log('[FMAlt] « ' + alt + ' » -> commune');
    }
    return '';
  }

  var FMAlt = {
    debug: false,
    /* Mots qui forcent « image commune à toutes les variantes ». Modifiable
       à chaud depuis la console : window.FMAlt.communs.push('universel'). */
    communs: ['all', 'tous', 'toutes', 'tout', 'commun', 'communs', 'commune', 'communes', 'global', 'globale'],
    normalise: normalise,
    mots: mots,
    match: match,
    /* Vrai si cet alt désigne CETTE variante-là. Raccourci des appelants. */
    correspond: function (alt, nom, noms) {
      var m = match(alt, noms);
      return !!m && normalise(m) === normalise(nom);
    }
  };

  window.FMAlt = FMAlt;
})();